import React from "react"
import { Dispatch } from "redux"

import {
    CcLibraryHeading,
    CcLibraryItem,
    CcLibrarySet,
    CcLibraryHeadingList,
    CcLibraryItemList,
} from "server/models"
import { ModalContent } from "client/parts/modal/styled"
import { ContentColumn } from "client/parts/layout"
import {
    PARAGRAPH, ITEM,
    OUTSIDE, INSIDE,
    BEFORE, AFTER,
} from "const"

import {
    LoadNestedItems,
    UpdateManyCcLibraryItems,
    UpdateManyCcLibraryHeadings,
    setCcLibraryItemList,
    setCcLibraryHeadingList,
    setCcLibraryItemNested,
} from "client/store"
import { TableGroupedIds, TableNestedIds } from "client/components"
import {
    ParagraphListWrapper,
    ParagraphWrapper,
    ParagraphContent,
    ParagraphCollapseWrapper,
    ItemListWrapper,
    ItemWrapper,
} from "./styled"


interface DragObjectType {
    elem: Element
    avatar?: HTMLElement
    downX: number
    downY: number
    startItem: CcLibraryHeading | CcLibraryItem
    endItem?: CcLibraryHeading | CcLibraryItem
    startType: typeof PARAGRAPH | typeof ITEM
    endType?: typeof PARAGRAPH | typeof ITEM
    align?: typeof OUTSIDE | typeof INSIDE
    position?: typeof BEFORE | typeof AFTER
    endParagraphList?: readonly TableGroupedIds[],
    endItemList?: readonly TableNestedIds[],
    scrollableParent: Element
    prev: Element
    next: Element
    parent: Element
    isDragging: boolean
}
let dragObject: DragObjectType = null
let scrollTimer: ReturnType<typeof setTimeout>

let set: CcLibrarySet
let headingList: CcLibraryHeadingList
let itemList: CcLibraryItemList
let dispatch: Dispatch


export const onMouseDown = (
    e: React.MouseEvent<Element, MouseEvent>,
    startItem: CcLibraryHeading | CcLibraryItem,
    startType: typeof PARAGRAPH | typeof ITEM,
    setObject: CcLibrarySet,
    headings: CcLibraryHeadingList,
    items: CcLibraryItemList,
    disp: Dispatch,
) => {
    set = setObject
    headingList = headings
    itemList = items
    dispatch = disp

    if (e.button !== 0) return // not left button is pressed
    const target = e.target as HTMLElement

    let elem
    if (startType === PARAGRAPH) elem = target.closest(ParagraphWrapper)
    if (startType === ITEM) elem = target.closest(ItemWrapper)

    let scrollableParent = elem.closest(ModalContent)
    if (!scrollableParent) scrollableParent = elem.closest(ContentColumn)

    dragObject = {
        elem,
        downX: e.pageX,
        downY: e.pageY,
        startItem,
        startType,
        scrollableParent,
        prev: elem.previousElementSibling,
        next: elem.nextElementSibling,
        parent: elem.parentElement,
        isDragging: true,
    }

    document.body.addEventListener("mousemove", onMouseMove)
    document.body.addEventListener("mouseup", onMouseUp)
}

export const onMouseMove = (e: MouseEvent) => {
    if (!dragObject || !dragObject.isDragging) return // no element

    if (!dragObject.avatar) { // create avatar and start moving
        // prevent tiny movement
        const moveX = e.pageX - dragObject.downX
        const moveY = e.pageY - dragObject.downY
        if (Math.abs(moveX) < 3 && Math.abs(moveY) < 3) return

        const elem = dragObject.elem as HTMLElement
        const width = elem.offsetWidth
        const height = elem.offsetHeight

        const avatar = elem.cloneNode(true) as HTMLElement
        const items = avatar.querySelector(ItemListWrapper)
        const paragraphCollapse = avatar.querySelector(ParagraphCollapseWrapper)
        const paragraphContent = avatar.querySelector(ParagraphContent)

        if (items) items.remove()
        if (paragraphCollapse) paragraphCollapse.remove()
        if (paragraphContent) paragraphContent.remove()

        dragObject.avatar = avatar
        dragObject.avatar.classList.add("drag-item")
        dragObject.avatar.style.width = `${width}px`
        dragObject.avatar.style.height = `${height}px`

        document.body.append(dragObject.avatar)
        document.body.classList.add("dragging")
        dragObject.elem.classList.add("ghost")
        if (dragObject.startType === PARAGRAPH) {
            dragObject.elem.classList.add("collapsed")
        }
    }

    const top = e.pageY - 16
    const left = e.pageX - 17
    dragObject.avatar.style.left = `${left}px`
    dragObject.avatar.style.top = `${top}px`

    scrollPage(e)
}

export const scrollPage = (e: MouseEvent) => {
    const edgeSize = 80
    const edgeTop = edgeSize
    const edgeBottom = window.innerHeight - edgeSize

    if (e.pageY > edgeTop && e.pageY < edgeBottom) {
        clearTimeout(scrollTimer)
    } else {
        const scroll = () => {
            clearTimeout(scrollTimer)
            const { scrollableParent } = dragObject
            const maxScroll = scrollableParent.scrollHeight
            const currentScroll = scrollableParent.scrollTop
            const maxStep = 50
            let intensity

            let nextScroll = currentScroll

            if (e.pageY < edgeTop && currentScroll > 0) { // scroll up
                intensity = ((edgeTop - e.pageY) / edgeSize)
                nextScroll -= (maxStep * intensity)
            } else if (e.pageY > edgeBottom && currentScroll < maxScroll) { // scroll down
                intensity = ((e.pageY - edgeBottom) / edgeSize)
                nextScroll += (maxStep * intensity)
            }
            nextScroll = Math.max(0, Math.min(maxScroll, nextScroll))

            if (nextScroll !== currentScroll) {
                scrollableParent.scrollTo(0, nextScroll)
                scrollTimer = setTimeout(scroll, 30)
            }
        }
        scroll()
    }
}

export const onMouseEnter = (
    e: React.MouseEvent<Element, MouseEvent>,
    endItem: CcLibraryHeading | CcLibraryItem,
    endType: typeof PARAGRAPH | typeof ITEM,
    align: typeof OUTSIDE | typeof INSIDE,
    endParagraphList: readonly TableGroupedIds[],
    endItemList: readonly TableNestedIds[],
) => {
    if (!dragObject || !dragObject.isDragging) return // no element
    const { startItem, startType } = dragObject
    const elem = e.target as HTMLElement
    let target
    let paragraph

    const setDragObjectData = () => {
        dragObject.endItem = endItem
        dragObject.endType = endType
        dragObject.align = align
        dragObject.endParagraphList = endParagraphList
        dragObject.endItemList = endItemList
    }

    if (startType === PARAGRAPH && endType === PARAGRAPH && endItem.id !== startItem.id) {
        setDragObjectData()

        if (align === OUTSIDE) {
            target = elem.closest(ParagraphWrapper)
            if (target.previousElementSibling !== dragObject.elem) {
                target.before(dragObject.elem)
                dragObject.position = BEFORE
            } else {
                target.after(dragObject.elem)
                dragObject.position = AFTER
            }
        }

        if (align === INSIDE) {
            paragraph = elem.closest(ParagraphWrapper)
            paragraph.classList.remove("collapsed")
            target = paragraph.querySelector(ParagraphContent).querySelector(ParagraphListWrapper)
            target.prepend(dragObject.elem)
        }
    }

    if (startType === ITEM && endType === ITEM && endItem.id !== startItem.id) {
        setDragObjectData()

        if (align === OUTSIDE) {
            target = elem.closest(ItemWrapper)
            if (target.previousElementSibling !== dragObject.elem) {
                target.before(dragObject.elem)
                dragObject.position = BEFORE
            } else {
                target.after(dragObject.elem)
                dragObject.position = AFTER
            }
        }

        if (align === INSIDE) {
            const item = elem.closest(ItemWrapper)
            const requirements = item.querySelector(ItemListWrapper)
            requirements.prepend(dragObject.elem)
        }
    }

    if (startType === ITEM && endType === PARAGRAPH) {
        setDragObjectData()

        if (align === OUTSIDE) {
            target = elem.closest(ParagraphWrapper).closest(ParagraphContent).querySelector(ItemListWrapper)
            target.append(dragObject.elem)
        }

        if (align === INSIDE) {
            paragraph = elem.closest(ParagraphWrapper)
            paragraph.classList.remove("collapsed")
            target = paragraph.querySelector(ParagraphContent).querySelector(ItemListWrapper)
            target.prepend(dragObject.elem)
        }
    }
}

export const onMouseUp = () => {
    if (!dragObject) return // no element
    if (dragObject.avatar) dragObject.avatar.remove()
    dragObject.isDragging = false

    sort()
    clearTimeout(scrollTimer)
}

export const clearDragObject = () => {
    if (!dragObject) return // no element

    const elem = dragObject.elem as HTMLElement
    elem.classList.remove("ghost")

    const { prev, next, parent } = dragObject
    if (prev) prev.after(elem)
    else if (next) next.before(elem)
    else if (parent) parent.append(elem)

    dragObject = null
    document.body.classList.remove("dragging")
    document.body.querySelector(ParagraphListWrapper).classList.remove("disabled")

    document.body.removeEventListener("mousemove", onMouseMove)
    document.body.removeEventListener("mouseup", onMouseUp)
}

export const sort = () => {
    const {
        startItem,
        startType,
        endItem,
        endType,
        align,
        position,
        endParagraphList,
        endItemList,
    } = dragObject

    if (!startItem || !startType || !endItem || !endType) {
        clearDragObject()
        return
    }

    document.body.querySelector(ParagraphListWrapper).classList.add("disabled")

    const updateItems: CcLibraryItem[] = []
    const updateHeadings: CcLibraryHeading[] = []

    // sort elements
    if (align === OUTSIDE) {
        if (startType === ITEM && endType === ITEM) {
            const itemStart = startItem as CcLibraryItem
            const itemEnd = endItem as CcLibraryItem

            const ordered: number[] = []
            endItemList.forEach((row) => {
                if (row.id === itemStart.id) return
                if (row.id !== itemEnd.id) ordered.push(Number(row.id))
                if (row.id === itemEnd.id && position === BEFORE) {
                    ordered.push(itemStart.id)
                    ordered.push(row.id)
                }
                if (row.id === itemEnd.id && position === AFTER) {
                    ordered.push(row.id)
                    ordered.push(itemStart.id)
                }
            })

            ordered.forEach((id, index) => {
                const item: CcLibraryItem = {
                    ...itemList[id],
                    headingId: itemEnd.headingId,
                    itemId: itemEnd.itemId,
                    order: index,
                }

                updateItems.push(item)
            })
        }

        if (startType === PARAGRAPH && endType === PARAGRAPH) {
            const headingStart = startItem as CcLibraryHeading
            const headingEnd = endItem as CcLibraryHeading

            const ordered: number[] = []
            endParagraphList.forEach((row) => {
                if (row.groupId === headingStart.id) return
                if (row.groupId !== headingEnd.id) ordered.push(row.groupId)
                if (row.groupId === headingEnd.id && position === BEFORE) {
                    ordered.push(headingStart.id)
                    ordered.push(row.groupId)
                }
                if (row.groupId === headingEnd.id && position === AFTER) {
                    ordered.push(row.groupId)
                    ordered.push(headingStart.id)
                }
            })

            ordered.forEach((id, index) => {
                const heading: CcLibraryHeading = {
                    ...headingList[id],
                    headingId: headingEnd.headingId,
                    order: index,
                }

                updateHeadings.push(heading)
            })
        }

        if (startType === ITEM && endType === PARAGRAPH) {
            const itemStart = startItem as CcLibraryItem
            const headingEnd = endItem as CcLibraryHeading

            // move items to the end of the parent paragraph
            const item: CcLibraryItem = {
                ...itemStart,
                headingId: headingEnd.headingId,
                itemId: null,
                order: endItemList.length
                    ? itemList[Number(endItemList[endItemList.length - 1].id)].order + 1
                    : 0,
            }

            updateItems.push(item)
        }
    }

    if (align === INSIDE) {
        if (startType === ITEM && endType === ITEM) {
            const itemStart = startItem as CcLibraryItem
            const itemEnd = endItem as CcLibraryItem

            // put selected item to the top of the target list
            const ordered: number[] = [itemStart.id]
            endItemList.forEach((row) => {
                if (row.id === itemStart.id) return
                ordered.push(Number(row.id))
            })

            ordered.forEach((id, index) => {
                const item: CcLibraryItem = {
                    ...itemList[id],
                    headingId: null,
                    itemId: itemEnd.id,
                    order: index,
                }

                updateItems.push(item)
            })
        }

        if (startType === PARAGRAPH && endType === PARAGRAPH) {
            const headingStart = startItem as CcLibraryHeading
            const headingEnd = endItem as CcLibraryHeading

            // put selected heading to the top of the list
            const ordered: number[] = [headingStart.id]
            endParagraphList.forEach((row) => {
                if (row.groupId === headingStart.id) return
                ordered.push(row.groupId)
            })

            ordered.forEach((id, index) => {
                const heading: CcLibraryHeading = {
                    ...headingList[id],
                    headingId: headingEnd.id,
                    order: index,
                }

                updateHeadings.push(heading)
            })
        }

        if (startType === ITEM && endType === PARAGRAPH) {
            const itemStart = startItem as CcLibraryItem
            const headingEnd = endItem as CcLibraryHeading

            // put selected item to the top of the list
            const ordered: number[] = [itemStart.id]
            endItemList.forEach((row) => {
                if (row.id === itemStart.id) return
                ordered.push(Number(row.id))
            })

            ordered.forEach((id, index) => {
                const item: CcLibraryItem = {
                    ...itemList[id],
                    headingId: headingEnd.id,
                    itemId: null,
                    order: index,
                }

                updateItems.push(item)
            })
        }
    }

    Promise.all([
        UpdateManyCcLibraryItems(updateItems),
        UpdateManyCcLibraryHeadings(updateHeadings),
    ]).then(() => {
        LoadNestedItems(set.id, set.projectId, set.accountId)
        .then((res) => {
            clearDragObject()
            dispatch(setCcLibraryItemList(res.itemList))
            dispatch(setCcLibraryHeadingList(res.headingList))
            dispatch(setCcLibraryItemNested(res.nested))
        })
    })
}
